// Pure helpers that keep broadcasts consistent with each other and with the content
// they play. Nothing here touches Firebase: the callers write the result with
// `updateMany` (lib/firebase.js) as ONE atomic multi-path update.
//
// Rules:
// - duration = sum of the durations of every playlist entry. The same content (or
//   broadcast) can be in a playlist several times and counts every time; an entry
//   that no longer exists counts as 0.
// - a multiplier copy (`sourceId`) follows its original: name, chyron and content
//   list (repeated `multiplier` times), and with it the duration.
// - a composite broadcast (`broadcastList`) lasts as long as the broadcasts in it,
//   also when one of them is itself a composite.

const DEFAULTS = { showChyron: false }

// An empty list is the same as no list: the database does not store empty lists, so a regular
// broadcast without content has no `contentList` at all
const normalize = (key, value) => {
  if (Array.isArray(value) && value.length === 0) return null
  return value ?? DEFAULTS[key] ?? null
}

const sameValue = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((value, i) => value === b[i])
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 1e-6
  }
  return a === b
}

// Seconds keep millisecond precision (the browser and ffprobe report fractions), but
// without float noise such as 0.1 + 0.2 = 0.30000000000000004.
export const roundSeconds = (value) => Math.round((Number(value) || 0) * 1000) / 1000

// Total seconds of a list of ids. `durationOf(id)` may return undefined (unknown id).
export const sumDurations = (ids, durationOf) =>
  roundSeconds((ids || []).reduce((total, id) => total + (durationOf(id) || 0), 0))

export const repeatList = (list, times) =>
  Array.from({ length: Math.max(1, times || 1) }, () => list || []).flat()

// Duration of one broadcast record, from the current content / broadcast data.
// Returns null when that data is not available (nothing loaded), so callers can keep
// the duration they already have instead of writing 0.
export const computeDuration = (record, { broadcasts = [], contentItems = [] }) => {
  if (record.broadcastList) {
    if (broadcasts.length === 0) return null
    const byId = new Map(broadcasts.map(b => [b.id, b]))
    return sumDurations(record.broadcastList, id => byId.get(id)?.duration)
  }
  if (contentItems.length === 0) return (record.contentList?.length > 0) ? null : 0
  const byId = new Map(contentItems.map(c => [c.id, c]))
  return sumDurations(record.contentList, id => byId.get(id)?.duration)
}

// The existing multiplier copy of `sourceId` with this multiplier, if there is one.
// Reusing it means a multiplier is never created twice.
export const findCopy = (broadcasts, sourceId, multiplier) =>
  broadcasts.find(b => b.sourceId === sourceId && (b.multiplier || 1) === multiplier)

// Works out what has to change in the OTHER broadcasts when
// - `edited` (a broadcast record, as it is about to be saved) is saved, and/or
// - the duration of the content `changedContent` ({ id, duration }) changes.
//
// Returns:
// - `edited`: the edited record with its duration recalculated (undefined without `edited`)
// - `updates`: multi-path updates for the other broadcasts ("broadcast/<id>/<field>").
//   Only fields that really changed are included, and `updated_at` is set only on
//   those broadcasts, so "Last update" does not move for nothing.
// `edited` itself is NOT in `updates`: the caller writes it under "broadcast/<id>".
export const planBroadcastUpdates = ({ broadcasts, contentItems, edited, changedContent, now = new Date().toISOString() }) => {
  const contentDuration = new Map(contentItems.map(c => [c.id, c.duration || 0]))
  if (changedContent) {
    contentDuration.set(changedContent.id, changedContent.duration || 0)
  }

  // Working copies, so the records held by the app state are never modified
  const byId = new Map(broadcasts.map(b => [b.id, { ...b }]))
  if (edited) {
    byId.set(edited.id, { ...edited })
  }

  const calcDuration = (b) => b.broadcastList
    ? sumDurations(b.broadcastList, id => byId.get(id)?.duration)
    : sumDurations(b.contentList, id => contentDuration.get(id))

  const updates = {}
  const changed = new Set() // broadcasts whose content or duration changed

  // Sets the fields that differ; returns true when something changed
  const apply = (b, fields) => {
    let touched = false
    for (const [key, value] of Object.entries(fields)) {
      const next = normalize(key, value)
      if (sameValue(normalize(key, b[key]), next)) continue
      updates[`broadcast/${b.id}/${key}`] = next
      b[key] = next
      touched = true
    }
    if (touched) {
      updates[`broadcast/${b.id}/updated_at`] = now
      b.updated_at = now
      changed.add(b.id)
    }
    return touched
  }

  // 1. The edited broadcast. When the data needed to calculate its duration is not
  // loaded, the duration from the form is kept instead of being written as 0.
  if (edited) {
    const record = byId.get(edited.id)
    const dataLoaded = record.broadcastList ? byId.size > 1 : contentDuration.size > 0
    if (dataLoaded || !(record.contentList?.length > 0)) {
      record.duration = calcDuration(record)
    }
    changed.add(record.id)
  }

  // 2. Content with a new duration: every broadcast that plays it
  if (changedContent) {
    for (const b of byId.values()) {
      if (b.id !== edited?.id && b.contentList?.includes(changedContent.id)) {
        apply(b, { duration: calcDuration(b) })
      }
    }
  }

  // 3. Multiplier copies follow their original. The copy's duration is summed from its
  // own (repeated) list, so it always matches what it plays.
  for (const b of byId.values()) {
    if (b.id === edited?.id) continue
    const source = b.sourceId && changed.has(b.sourceId) ? byId.get(b.sourceId) : null
    // Only a regular broadcast has copies; it can be empty (no `contentList`), then so are they
    if (!source || source.broadcastList) continue
    const contentList = repeatList(source.contentList, b.multiplier || 1)
    apply(b, {
      name: source.name,
      showChyron: !!source.showChyron,
      resolutions: source.resolutions,
      contentList,
      duration: sumDurations(contentList, id => contentDuration.get(id)),
    })
  }

  // 4. Composite broadcasts: repeat until nothing changes (nested composites need
  // several passes; the pass limit also protects against a broadcast listing itself).
  for (let pass = 0; pass < byId.size; pass++) {
    let progressed = false
    for (const b of byId.values()) {
      if (b.id === edited?.id || !b.broadcastList) continue
      if (!b.broadcastList.some(id => changed.has(id))) continue
      if (apply(b, { duration: calcDuration(b) })) progressed = true
    }
    if (!progressed) break
  }

  return { edited: edited ? byId.get(edited.id) : undefined, updates }
}
